import { useState } from 'react';
import Image from 'next/image';
import Head from 'next/head';
import Link from 'next/link';
import SEO from '../components/SEO';
import ContactUsCTA from '../components/ContactUsCTA';
import styles from '../styles/OurCoreTechnology.module.css';

import {
  SEO_META,
  HERO,
  HOW_IT_WORKS,
  INFRASTRUCTURE,
  WHY_CHOOSE,
  FAQ_ITEMS,
  CLOSING_CTA,
} from '../const/our-core-technology';

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQ_ITEMS.map((item) => ({
    '@type': 'Question',
    name: item.Q,
    acceptedAnswer: { '@type': 'Answer', text: item.A.join(' ') },
  })),
};

const CheckIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="15"
    height="15"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const ExternalIcon = ({ size = 14 }) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </svg>
);

const HOW_ICONS = {
  // Section 1 — richer, purpose-built icons for the four technology points.
  neuralnet: (
    <>
      <circle cx="5" cy="6" r="1.8" />
      <circle cx="5" cy="18" r="1.8" />
      <circle cx="12" cy="12" r="1.9" />
      <circle cx="19" cy="6" r="1.8" />
      <circle cx="19" cy="18" r="1.8" />
      <line x1="6.6" y1="6.9" x2="10.4" y2="11.1" />
      <line x1="6.6" y1="17.1" x2="10.4" y2="12.9" />
      <line x1="13.6" y1="11.1" x2="17.4" y2="6.9" />
      <line x1="13.6" y1="12.9" x2="17.4" y2="17.1" />
    </>
  ),
  chip: (
    <>
      <rect x="5" y="5" width="14" height="14" rx="2.5" />
      <rect x="9.5" y="9.5" width="5" height="5" rx="1" />
      <path d="M9 2v3M15 2v3M9 19v3M15 19v3M2 9h3M2 15h3M19 9h3M19 15h3" />
    </>
  ),
  dualmedia: (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2.5" />
      <circle cx="8" cy="10" r="1.6" />
      <path d="M3 16.5l4-3.2 2.4 1.9" />
      <path d="M12.8 10.1l3.6 2.05-3.6 2.05z" />
    </>
  ),
  training: (
    <>
      <path d="M12 4 2 9l10 5 10-5-10-5z" />
      <path d="M6 11.2V15c0 1.1 2.7 2.6 6 2.6s6-1.5 6-2.6v-3.8" />
      <path d="M22 9v5" />
    </>
  ),
  // Section 2 — per-point icons.
  eyeOff: (
    <>
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 10 8 10 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <path d="M6.61 6.61A18.5 18.5 0 0 0 2 12s3 8 10 8a9.12 9.12 0 0 0 5.39-1.61" />
      <line x1="2" y1="2" x2="22" y2="22" />
    </>
  ),
  certificate: (
    <>
      <circle cx="12" cy="9" r="6" />
      <path d="M9 14.5 7.5 22l4.5-2.5L16.5 22 15 14.5" />
      <path d="m9.5 9 1.6 1.6L14.5 7.5" />
    </>
  ),
  lock: (
    <>
      <rect x="4" y="11" width="16" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
      <circle cx="12" cy="16" r="1.2" />
    </>
  ),
  cloud: <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z" />,
  server: (
    <>
      <rect x="3" y="4" width="18" height="7" rx="1.5" />
      <rect x="3" y="13" width="18" height="7" rx="1.5" />
      <line x1="7" y1="7.5" x2="7.01" y2="7.5" />
      <line x1="7" y1="16.5" x2="7.01" y2="16.5" />
    </>
  ),
  activity: <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />,
  mapPin: (
    <>
      <path d="M21 10c0 7-9 12-9 12s-9-5-9-12a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </>
  ),
  shieldCheck: (
    <>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <path d="m9 11.5 2 2 4-4" />
    </>
  ),
  network: (
    <>
      <circle cx="18" cy="5" r="3" />
      <circle cx="6" cy="12" r="3" />
      <circle cx="18" cy="19" r="3" />
      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
      <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
    </>
  ),
  cpu: (
    <>
      <rect x="4" y="4" width="16" height="16" rx="2" />
      <rect x="9" y="9" width="6" height="6" />
      <line x1="9" y1="1" x2="9" y2="4" />
      <line x1="15" y1="1" x2="15" y2="4" />
      <line x1="9" y1="20" x2="9" y2="23" />
      <line x1="15" y1="20" x2="15" y2="23" />
      <line x1="20" y1="9" x2="23" y2="9" />
      <line x1="20" y1="14" x2="23" y2="14" />
      <line x1="1" y1="9" x2="4" y2="9" />
      <line x1="1" y1="14" x2="4" y2="14" />
    </>
  ),
  video: (
    <>
      <polygon points="23 7 16 12 23 17 23 7" />
      <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
    </>
  ),
  award: (
    <>
      <circle cx="12" cy="8" r="7" />
      <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88" />
    </>
  ),
  shield: <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />,
  globe: (
    <>
      <circle cx="12" cy="12" r="10" />
      <line x1="2" y1="12" x2="22" y2="12" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </>
  ),
  target: (
    <>
      <circle cx="12" cy="12" r="10" />
      <circle cx="12" cy="12" r="6" />
      <circle cx="12" cy="12" r="2" />
    </>
  ),
  code: (
    <>
      <polyline points="16 18 22 12 16 6" />
      <polyline points="8 6 2 12 8 18" />
    </>
  ),
  layers: (
    <>
      <polygon points="12 2 2 7 12 12 22 7 12 2" />
      <polyline points="2 17 12 22 22 17" />
      <polyline points="2 12 12 17 22 12" />
    </>
  ),
  truck: (
    <>
      <rect x="1" y="3" width="15" height="13" />
      <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
      <circle cx="5.5" cy="18.5" r="2.5" />
      <circle cx="18.5" cy="18.5" r="2.5" />
    </>
  ),
};

const HowIcon = ({ name, size = 30, strokeWidth = 1.8 }) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {HOW_ICONS[name] || HOW_ICONS.network}
  </svg>
);

const renderText = (segments, linkClass) =>
  segments.map((seg, i) =>
    seg.link ? (
      <Link key={i} href={seg.link}>
        {seg.newTab ? (
          <a className={linkClass} target="_blank" rel="noopener noreferrer">
            {seg.text}
            <span className={styles.inlineExtIcon}>
              <ExternalIcon size={12} />
            </span>
          </a>
        ) : (
          <a className={linkClass}>{seg.text}</a>
        )}
      </Link>
    ) : (
      <span key={i}>{seg.text}</span>
    )
  );

const QuestionSection = ({ keyIndex, que, ans }) => {
  const [isOpen, setIsOpen] = useState(keyIndex === 0);
  return (
    <div className={styles.questionModal} onClick={() => setIsOpen(!isOpen)}>
      <div className={styles.question}>
        <p>{que}</p>
        <span className={styles.faqToggle}>
          <Image
            src={isOpen ? '/img/shrink-icon.svg' : '/img/expand-icon.svg'}
            alt="FAQ"
            width={28}
            height={28}
            objectFit="contain"
          />
        </span>
      </div>
      <div className={`${styles.answer} ${isOpen ? styles.open : ''}`}>
        {ans.map((item, index) => (
          <p key={index}>{item}</p>
        ))}
      </div>
    </div>
  );
};

export default function OurCoreTechnology() {
  return (
    <div className={styles.container}>
      <SEO
        title={SEO_META.title}
        description={SEO_META.description}
        image="https://inspektlabs.com/img/car.png"
        url="https://inspektlabs.com/our-core-technology"
        page="our-core-technology"
      />
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
        />
      </Head>

      <main className={styles.main}>
        {/* Hero */}
        <section className={styles.heroSection}>
          <div className={styles.heroContent}>
            <nav className={styles.breadcrumb} aria-label="Breadcrumb">
              <Link href="/">
                <a className={styles.crumbLink}>Home</a>
              </Link>
              <span className={styles.crumbSep} aria-hidden="true">
                ›
              </span>
              <span className={styles.crumbCurrent}>Our Core Technology</span>
            </nav>
            <h1 className={styles.heroTitle}>{HERO.title}</h1>
            <p className={styles.heroText}>{HERO.body}</p>
            <div className={styles.heroCtaRow}>
              <ContactUsCTA
                as="div"
                cluster="core-technology-hero"
                className={styles.heroBtn}
              >
                {HERO.primaryCta}
              </ContactUsCTA>
              <Link href={HERO.secondaryLink}>
                <a
                  className={styles.heroBtnSecondary}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {HERO.secondaryCta}
                  <ExternalIcon />
                </a>
              </Link>
            </div>
          </div>
        </section>

        {/* How it works + technology behind — one blue band */}
        <section className={styles.howSection}>
          <div className={styles.howPanel}>
            <h2 className={styles.howHeading}>{HOW_IT_WORKS.title}</h2>
            <p className={styles.howIntro}>{HOW_IT_WORKS.body}</p>
            <div className={styles.howGrid}>
              {HOW_IT_WORKS.items.map((item) => (
                <div key={item.title} className={styles.howItem}>
                  <div className={styles.howCircle}>
                    <HowIcon name={item.icon} size={54} strokeWidth={1.6} />
                  </div>
                  <div className={styles.howCaption}>
                    <p className={styles.howCaptionTitle}>{item.title}</p>
                    <p className={styles.howCaptionBody}>{item.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Infrastructure — blue band, two columns */}
        <section className={styles.infraSection}>
          <div className={styles.infraInner}>
            <h2 className={styles.howHeading}>{INFRASTRUCTURE.title}</h2>
            <p className={styles.howIntro}>{INFRASTRUCTURE.body}</p>
            <div className={styles.infraCols}>
              {INFRASTRUCTURE.columns.map((col) => (
                <div key={col.heading} className={styles.infraCol}>
                  <h3 className={styles.infraColHeading}>{col.heading}</h3>
                  <div className={styles.infraPoints}>
                    {col.points.map((point) => (
                      <div key={point.title} className={styles.infraPoint}>
                        <div className={styles.infraCircle}>
                          <HowIcon name={point.icon} />
                        </div>
                        <div className={styles.infraCard}>
                          <p className={styles.infraCardTitle}>{point.title}</p>
                          <p className={styles.infraCardBody}>{point.body}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why choose — blue band, circles + caption cards */}
        <section className={styles.howSection}>
          <div className={styles.howPanel}>
            <h2 className={styles.howHeading}>{WHY_CHOOSE.title}</h2>
            <p className={styles.howIntro}>
              {renderText(WHY_CHOOSE.body, styles.inlineLink)}
            </p>
            <div className={styles.whyGrid}>
              {WHY_CHOOSE.cards.map((card) => (
                <div key={card.title} className={styles.howItem}>
                  <div className={styles.howCircle}>
                    <HowIcon name={card.icon} size={54} strokeWidth={1.6} />
                  </div>
                  <div className={styles.howCaption}>
                    <p className={styles.howCaptionTitle}>{card.title}</p>
                    <p className={styles.howCaptionBody}>{card.body}</p>
                    {card.link && (
                      <Link href={card.link}>
                        <a className={styles.captionLink}>{card.linkText} →</a>
                      </Link>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className={styles.faqSectionContainer}>
          <div className={styles.faqBox}>
            <div className={styles.faqBoxHeading}>
              <h2 className={styles.faqTitle}>Frequently Asked Questions</h2>
            </div>
            <div className={styles.questionContainer}>
              {FAQ_ITEMS.map((item, index) => (
                <QuestionSection
                  key={item.Q}
                  keyIndex={index}
                  que={item.Q}
                  ans={item.A}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Bottom CTA */}
        <section className={styles.ctaSection}>
          <h2 className={styles.ctaTitle}>{CLOSING_CTA.title}</h2>
          <p className={styles.ctaText}>{CLOSING_CTA.body}</p>
          <ContactUsCTA
            as="div"
            cluster="core-technology-footer"
            className={styles.ctaBtn}
          >
            {CLOSING_CTA.cta}
          </ContactUsCTA>
        </section>
      </main>
    </div>
  );
}
